'use client';

import { useState, type ReactNode } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../components/styled/text';
import { Collapsible } from '../../components/collapsible';
import { Clipboard } from '../../components/clipboard';
import { Tooltip } from '../../components/tooltip';
import { Badge } from '../../components/badge';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
} from '@terra/ui-icons';
import { HttpEditor } from '../http-editor';
import { MarkdownPreview } from '../markdown-preview';

export type EvidenceCardProps = {
  /** The raw HTTP request/response content */
  evidence: string;
  /** Explanation of what this evidence shows */
  explanation?: string;
  index: number;
  defaultExpanded?: boolean;
  actions?: ReactNode;
};

/**
 * Parse evidence string to extract HTTP method, URL, and status code
 */
function parseEvidenceHeader(evidence: string): {
  method?: string;
  url?: string;
  statusCode?: number;
  isRequest: boolean;
} {
  const lines = evidence.split('\n');
  const firstLine = lines[0]?.trim() || '';
  // Check if it's a request (starts with HTTP method)
  const requestMatch = firstLine.match(
    /^(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\s+(\S+)/i,
  );

  if (requestMatch) {
    return {
      method: requestMatch[1].toUpperCase(),
      url: requestMatch[2],
      isRequest: true,
    };
  }

  // Check if it's a response (starts with HTTP version and status)
  const responseMatch = firstLine.match(/^HTTP\/[\d.]+\s+(\d+)/i);

  if (responseMatch) {
    return {
      statusCode: parseInt(responseMatch[1], 10),
      isRequest: false,
    };
  }

  return { isRequest: true };
}

/**
 * Split evidence into request and response parts
 */
function splitRequestResponse(evidence: string): {
  request?: string;
  response?: string;
} {
  // Look for separator pattern (--- or empty line before HTTP response)
  const separatorPatterns = [
    /\n---\n/,
    /\n\nHTTP\/\d/,
    /\r\n---\r\n/,
    /\r\n\r\nHTTP\/\d/,
  ];

  for (const pattern of separatorPatterns) {
    const match = evidence.match(pattern);

    if (match && match.index !== undefined) {
      // Calculate separator length based on pattern type:
      // - For '---' separators: skip the entire match (includes the dashes)
      // - For HTTP patterns: only skip the newlines, keep the 'HTTP/...' part
      const separatorLength = match[0].includes('---')
        ? match[0].length
        : match[0].startsWith('\r\n\r\n')
          ? 4
          : 2;

      return {
        request: evidence.substring(0, match.index).trim(),
        response: evidence.substring(match.index + separatorLength).trim(),
      };
    }
  }

  // Check if it's just a request or just a response
  const header = parseEvidenceHeader(evidence);

  if (header.isRequest) {
    return { request: evidence.trim() };
  }

  return { response: evidence.trim() };
}

export function EvidenceCard({
  evidence,
  explanation,
  index,
  defaultExpanded = false,
  actions,
}: EvidenceCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { request, response } = splitRequestResponse(evidence);
  const requestHeader = request ? parseEvidenceHeader(request) : undefined;
  const responseHeader = response ? parseEvidenceHeader(response) : undefined;

  // Create a summary for the collapsed state
  // const summary = requestHeader?.url
  //   ? `${requestHeader.method || 'REQUEST'} ${requestHeader.url}`
  //   : `Evidence ${index + 1}`;
  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
    >
      <Box
        w="full"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        overflow="hidden"
        bg="bg.canvas"
      >
        {/* Explanation - always visible on top */}
        {explanation && (
          <Box px={3} py={2} color="fg.default">
            <MarkdownPreview source={explanation} fontSize={12} compact />
          </Box>
        )}

        {/* Header */}
        <Collapsible.Trigger asChild>
          <HStack
            w="full"
            gap={2}
            px={3}
            py={2}
            cursor="pointer"
            bg="bg.subtle"
            _hover={{ bg: 'bg.muted' }}
            justifyContent="space-between"
          >
            <HStack gap={2} flex={1} minW={0} color="fg.muted">
              {isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}

              {/* Request method badge */}
              {requestHeader?.method && (
                <Badge size="sm" variant="emphasis">
                  {requestHeader.method}
                </Badge>
              )}

              {/* Response status badge */}
              {responseHeader?.statusCode && (
                <Badge size="sm" variant="fill">
                  {responseHeader.statusCode}
                </Badge>
              )}

              {/* URL or summary */}
              <Text
                fontSize="xs"
                fontFamily="mono"
                color="fg.default"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                flex={1}
              >
                {requestHeader?.url || `Evidence ${index + 1}`}
              </Text>
            </HStack>

            <HStack gap={1} flexShrink={0}>
              {actions}
              <Clipboard.Root value={evidence}>
                <Tooltip title="Copy evidence">
                  <Clipboard.Trigger asChild>
                    <Box
                      as="button"
                      aria-label="Copy evidence"
                      onClick={(event: React.MouseEvent) =>
                        event.stopPropagation()
                      }
                      h="5"
                      w="5"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      color="fg.muted"
                      borderRadius="xs"
                      _hover={{
                        color: 'fg.default',
                        bg: 'bg.subtle',
                      }}
                    >
                      <Clipboard.Indicator copied={<Tick01Icon size={12} />}>
                        <Copy01Icon size={12} />
                      </Clipboard.Indicator>
                    </Box>
                  </Clipboard.Trigger>
                </Tooltip>
              </Clipboard.Root>
            </HStack>
          </HStack>
        </Collapsible.Trigger>

        {/* Content */}
        <Collapsible.Content>
          <VStack gap={0} alignItems="stretch">
            {/* Request section */}
            {request && (
              <Box borderTopWidth="1px" borderColor="border.subtle">
                <HttpEditor
                  value={request}
                  readOnly
                  title="Request"
                  height="auto"
                  minHeight="100px"
                  showViewModeToggle={false}
                  showStatusBar={false}
                />
              </Box>
            )}

            {/* Response section */}
            {response && (
              <Box borderTopWidth="1px" borderColor="border.subtle">
                <HttpEditor
                  value={response}
                  readOnly
                  title="Response"
                  statusCode={responseHeader?.statusCode}
                  height="auto"
                  minHeight="100px"
                  showViewModeToggle={false}
                  showStatusBar={false}
                />
              </Box>
            )}
          </VStack>
        </Collapsible.Content>
      </Box>
    </Collapsible.Root>
  );
}
